import { makeObservable, action, computed, observable } from 'mobx';
import { ROUTES } from '../routes';
import { RootStore } from './root';
import { AccountData, AccountDataExtended } from '@wix/identity-fed-common';
import {
  loginAccountSelectorAccountSelected,
  loginAccountSelectorComponentLoad,
} from '@wix/bi-logger-hls2/v2';
import {
  AUTH_METHODS_BY_MAIL,
  SEARCH_PARAMS,
  WIX_HOME_URL,
  WIX_DEFAULT_PARENT_ACCOUNT_ID,
  WIX_PARENT_ACCOUNT_HINT,
} from '../utils/constants';
import { shouldContinueLoginInApp } from './constants';
import {
  AccountData as UserAccountData,
  UserAccountsDataByEmailResponse,
} from '../types';
import { HttpError } from '@wix/http-client';
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { CAPTCHA_ACTIONS } from './captcha';
import { AuthMethod, setLastUsedAuthMethod } from '../utils/lastUsedAuthMethod';

export enum AccountsFilter {
  SSO = 'SSO',
}

export class SelectAccountStore {
  private readonly rootStore: RootStore;
  public showError: boolean = false;
  public filter: string = '';
  private captchaAdded: boolean = false;
  public userAccountsDataByEmail?: UserAccountsDataByEmailResponse;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      showError: observable,
      filter: observable,
      userAccountsDataByEmail: observable,
      email: computed,
      showAccountSelector: computed,
      initialFetch: action,
      onErrorOccured: action,
      resetError: action,
      onAccountSelected: action,
      fetchSsoAccounts: action.bound,
      fetchAccounts: action.bound,
      onBackClicked: action.bound,
    });
    this.setAndHandleFilter();
  }

  get email() {
    return this.rootStore.userDataStore.email;
  }

  public onErrorOccured = () => {
    this.showError = true;
  };

  public resetError = () => {
    this.showError = false;
  };

  public onAccountSelectorLoaded = (accountsData: AccountData[]) => {
    this.rootStore.biLogger.report(
      loginAccountSelectorComponentLoad({
        account_ids: accountsData
          .map((account) => account.accountId)
          .toString(),
        parent_account_ids: accountsData
          .map((account) => account.parentAccountId)
          .filter((id) => id)
          .toString(),
        context: 'login',
      }),
    );
  };

  // The selector passes back the looked-up account, authenticators included.
  public onAccountSelected = async (
    account: AccountDataExtended &
      Pick<UserAccountData, 'userAuthenticatorsData'>,
  ) => {
    // If there is a parentAccountId, try to get an SSO url
    this.rootStore.biLogger.report(
      loginAccountSelectorAccountSelected({
        parent_account_id: account.parentAccountId,
        selected_account_id: account.accountId,
      }),
    );
    this.rootStore.userDataStore.setResolvedAccount(this.email, account);
    const { navigationStore } = this.rootStore;
    const { targetUrlForSso } = navigationStore;
    // The selected account is currently logged in
    if (account.loggedInUserData) {
      targetUrlForSso
        ? navigationStore.redirect(targetUrlForSso)
        : navigationStore.redirect(WIX_HOME_URL);
    } else {
      const ssoUrl = account.accountSsoSettings?.ssoUrl;
      if (ssoUrl) {
        // Same as the approve-and-continue path: written before we hand the
        // browser to the IDP.
        setLastUsedAuthMethod(AuthMethod.SSO);
        if (shouldContinueLoginInApp(this.rootStore.experiments)) {
          // Back from an earlier pick: its organization isn't this account's.
          navigationStore.restoreLandingOrganizationId();
        }
        const { locationParamsEntries } = navigationStore;
        this.rootStore.navigationStore.appendSearchParamToCurrentLocation(
          SEARCH_PARAMS.EMAIL,
          this.email,
        );
        const searchParams = {
          ...locationParamsEntries,
        };
        targetUrlForSso &&
          (searchParams[SEARCH_PARAMS.TARGET_URL] = targetUrlForSso);
        this.rootStore.navigationStore.redirect(ssoUrl, {
          searchParams,
        });
      } else if (shouldContinueLoginInApp(this.rootStore.experiments)) {
        return this.continueLoginInApp(account);
      } else {
        if (this.rootStore.isNewLoginApp) {
          this.rootStore.setIsLoading(false);
          return this.rootStore.loginStore.onNavigateToLogin();
        }
        navigationStore.goToMainLogin({
          email: this.email,
          parentHint: account.parentAccountId || WIX_PARENT_ACCOUNT_HINT,
        });
      }
    }
  };

  /**
   * Continues the login with the picked account without leaving the page, so
   * a login in progress keeps its state: a redirect to the login page drops
   * an IdP login waiting for its first factor, and the merge never finishes.
   */
  private continueLoginInApp(
    account: Pick<
      UserAccountData,
      'userAuthenticatorsData' | 'parentAccountId'
    >,
  ) {
    const {
      socialAuthStore,
      emailStepStore,
      ssoStore,
      loginStore,
      idpConnectionLoginStore,
      navigationStore,
    } = this.rootStore;
    this.rootStore.setIsLoading(false);
    navigationStore.setOrganizationId(
      account.parentAccountId || WIX_DEFAULT_PARENT_ACCOUNT_ID,
    );
    // No password to log in with: its social providers, as the email step
    // routes such an account.
    if (socialAuthStore.shouldProceedToSocialAuthOnly(account)) {
      // The picked account has no SSO link; drop one an earlier lookup left.
      ssoStore.setSsoPageUrl('');
      return emailStepStore.authHandlers[AUTH_METHODS_BY_MAIL.SOCIAL_ONLY]({
        socialConnectionsProviders:
          account.userAuthenticatorsData?.socialConnectionsProviders ?? [],
      });
    }
    loginStore.onNavigateToLogin(undefined, { isEmailReadOnly: true });
    // Routing to the selector cleared the form's IdP flag, but an IdP login
    // waiting for its first factor still continues on the password form.
    if (
      loginStore.loginFlow.tokenAt(this.email, StateType.REQUIRE_FIRST_FACTOR)
    ) {
      idpConnectionLoginStore.isIdpFirstFactorFlow = true;
    }
  }

  public async fetchSsoAccounts() {
    const res = await this.fetchAccounts();
    const userAccountsDataByEmail = res;
    if (!userAccountsDataByEmail?.accountsData) {
      return;
    }
    userAccountsDataByEmail.accountsData =
      this.rootStore.ssoStore.filterSsoAccounts(
        userAccountsDataByEmail.accountsData,
      );
    return userAccountsDataByEmail;
  }

  public async fetchAccounts() {
    const { userDataStore, apiStore } = this.rootStore;
    try {
      const recaptchaParams =
        await this.rootStore.captchaStore.handleRecaptchaExecution({
          captchaAdded: this.captchaAdded,
          action: CAPTCHA_ACTIONS.GET_USER_ACCOUNTS,
        });
      const res = await apiStore.getUserAccounts({
        email: userDataStore.email,
        ...recaptchaParams,
      });
      return res;
    } catch (error) {
      const serverErrorCode = (
        error as HttpError
      )?.response?.data.errorCode?.toString();
      this.captchaAdded =
        this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
          serverErrorCode,
          this.captchaAdded,
        );

      console.error(error);
      this.onErrorOccured();
    }
  }

  get showAccountSelector() {
    return (
      this.showError ||
      (this.userAccountsDataByEmail?.accountsData?.length ?? 0) > 1
    );
  }

  public setAndHandleFilter(filter?: AccountsFilter) {
    this.filter =
      filter ??
      this.rootStore.navigationStore.getQueryParam('filterAccounts') ??
      '';
  }

  public async initialFetch() {
    this.resetError();
    let fetchUserAccounts = this.fetchAccounts;
    if (this.filter === AccountsFilter.SSO) {
      fetchUserAccounts = this.fetchSsoAccounts;
    }
    this.userAccountsDataByEmail = await fetchUserAccounts();
    const { accountsData } = this.userAccountsDataByEmail ?? {
      accountsData: [],
    };
    if (!accountsData?.length || accountsData.length > 1) {
      return;
    }
    this.onAccountSelected({
      ...accountsData[0],
      loggedInUserData: this.userAccountsDataByEmail?.loggedInUserData,
    });
  }

  public enableAccess() {
    return !!this.email;
  }
  public onBackClicked() {
    this.userAccountsDataByEmail = undefined;
    this.setAndHandleFilter();
    this.rootStore.navigationStore.goBack();
  }

  get isSelectAccountVisible() {
    return (
      this.rootStore.navigationStore.currentRoute === ROUTES.SELECT_ACCOUNT
    );
  }
}
